// delivery-outbox.ts
//
// §7.4: records send INTENT before any delivery attempt, tracks
// pending|sent|uncertain. Real network send is P7's job — this phase only
// lays the foundation (intent record + status file), test 29.
//
// B2 idempotency-key derivation split into outbox-idempotency-key.ts to keep
// this file ≤200 lines — re-exported so callers only import ONE module.

import { join } from "node:path";
import { ensureDir, pathExists, readJson } from "../filesystem-helpers.js";
import { writeFileDurable } from "./durable-file-writer.js";

export { deriveOutboxIdempotencyKey } from "./outbox-idempotency-key.js";

/** `failed` is terminal (chat replies past their attempt cap) — never listed as pending. */
export type OutboxOperationStatus = "pending" | "sent" | "uncertain" | "failed";

/**
 * The content needed to (re)perform the send. P19a: before this existed an
 * op carried only `payload_hash`, so `OutboxSendFn(eventId)` — the only
 * signature the delivery worker offers — had NO way to reconstruct the
 * message. That made the "reconciliation-aware send" contract in
 * outbox-delivery-worker.ts literally unimplementable in production, which
 * is why no production caller ever existed. Optional so ops written before
 * P19a keep reading cleanly (they simply cannot be resent by a later tick).
 */
export interface OutboxPostPayload {
  channel_id: string;
  message: string;
  root_id?: string;
  correlation_id?: string;
}

export interface OutboxOperation {
  event_id: string;
  mission_id: string;
  sequence: number;
  kind: string;
  payload_hash: string;
  /** Undefined for ops recorded before P19a — see OutboxPostPayload. */
  payload?: OutboxPostPayload;
  /**
   * B2: stable intent key (deriveOutboxIdempotencyKey). Optional — ops
   * recorded before B2 have none and simply get no dedupe protection.
   */
  idempotency_key?: string;
  status: OutboxOperationStatus;
  /** Delivery attempts so far — only kinds that cap their retries keep it. */
  attempts?: number;
  remote_id?: string;
  created_at: string;
  updated_at: string;
}

export interface RecordOutboxIntentInput {
  controlRoot: string;
  eventId: string;
  missionId: string;
  sequence: number;
  kind: string;
  payloadHash: string;
  payload?: OutboxPostPayload;
  idempotencyKey?: string;
  now?: () => Date;
}

function outboxDir(controlRoot: string): string {
  return join(controlRoot, "outbox");
}

function operationPath(controlRoot: string, eventId: string): string {
  return join(outboxDir(controlRoot), `${eventId}.json`);
}

/**
 * Record intent BEFORE any send attempt — status starts "pending".
 *
 * B2: when `idempotencyKey` is given and an operation with that key already
 * exists, the EXISTING operation is returned unchanged and no second intent
 * is written — whatever its status. A `sent` op must not be sent twice; an
 * `uncertain`/`pending` one must not be duplicated into two deliveries
 * either (the rule is "failed does not re-run under the same key" — a
 * genuine retry is a NEW intent with a new key and a stated reason, see
 * workflows/autopilot/recovery.md).
 */
export async function recordOutboxIntent(input: RecordOutboxIntentInput): Promise<OutboxOperation> {
  if (input.idempotencyKey !== undefined) {
    const existing = await findOutboxOperationByIdempotencyKey(
      input.controlRoot,
      input.idempotencyKey,
    );
    if (existing) return existing;
  }
  const now = input.now ?? (() => new Date());
  const stamp = now().toISOString();
  const op: OutboxOperation = {
    event_id: input.eventId,
    mission_id: input.missionId,
    sequence: input.sequence,
    kind: input.kind,
    payload_hash: input.payloadHash,
    ...(input.payload ? { payload: input.payload } : {}),
    ...(input.idempotencyKey !== undefined ? { idempotency_key: input.idempotencyKey } : {}),
    status: "pending",
    created_at: stamp,
    updated_at: stamp,
  };
  await ensureDir(outboxDir(input.controlRoot));
  await saveOutboxOperation(input.controlRoot, op);
  return op;
}

/** Find an operation by its B2 intent key, whatever its status. */
export async function findOutboxOperationByIdempotencyKey(
  controlRoot: string,
  idempotencyKey: string,
): Promise<OutboxOperation | undefined> {
  const { promises: fs } = await import("node:fs");
  const dir = outboxDir(controlRoot);
  if (!(await pathExists(dir))) return undefined;
  for (const entry of await fs.readdir(dir)) {
    if (!entry.endsWith(".json")) continue;
    const op = await readJson<OutboxOperation>(join(dir, entry));
    if (op.idempotency_key === idempotencyKey) return op;
  }
  return undefined;
}

export async function readOutboxOperation(
  controlRoot: string,
  eventId: string,
): Promise<OutboxOperation | undefined> {
  const path = operationPath(controlRoot, eventId);
  if (!(await pathExists(path))) return undefined;
  return readJson<OutboxOperation>(path);
}

/** Update status AFTER a send attempt (sent/uncertain) — never before intent exists. */
export async function updateOutboxStatus(
  controlRoot: string,
  eventId: string,
  status: OutboxOperationStatus,
  remoteId: string | undefined,
  now: () => Date = () => new Date(),
): Promise<OutboxOperation> {
  const existing = await readOutboxOperation(controlRoot, eventId);
  if (!existing) {
    throw new Error(
      `updateOutboxStatus: không có intent nào cho event_id "${eventId}" — intent phải được ghi TRƯỚC khi gửi.`,
    );
  }
  const next: OutboxOperation = {
    ...existing,
    status,
    remote_id: remoteId ?? existing.remote_id,
    updated_at: now().toISOString(),
  };
  await saveOutboxOperation(controlRoot, next);
  return next;
}

/** Durable overwrite of one op file (the op must already have its event_id). */
export async function saveOutboxOperation(controlRoot: string, op: OutboxOperation): Promise<void> {
  await writeFileDurable(
    operationPath(controlRoot, op.event_id),
    Buffer.from(`${JSON.stringify(op, null, 2)}\n`, "utf8"),
  );
}

export async function listPendingOutboxOperations(controlRoot: string): Promise<OutboxOperation[]> {
  const { promises: fs } = await import("node:fs");
  const dir = outboxDir(controlRoot);
  if (!(await pathExists(dir))) return [];
  const entries = await fs.readdir(dir);
  const ops: OutboxOperation[] = [];
  for (const entry of entries) {
    if (!entry.endsWith(".json")) continue;
    const op = await readJson<OutboxOperation>(join(dir, entry));
    if (op.status === "pending" || op.status === "uncertain") ops.push(op);
  }
  return ops.sort((a, b) => a.sequence - b.sequence);
}
